'use client';
import React, { FC, memo } from 'react';
import styles from './index.module.scss';
import cls from 'classnames';
import { Button, getProcessedUrl } from '@bfe/moly';
import { safeToFormat1 as safeToFormat } from '@/utils/number';
import { useI18n } from '@spot-fe/base-ui';
import type { EndProject } from '@/apis';
import { completedProjectClick } from '@/config/sensor';
import AutoEllipsisText from '../AutoEllipsisText';
import { useText } from './text';

export interface Props {
  project: EndProject;
  isPc: boolean;
  onAction?: (info: EndProject) => void;
}

const EndProjectCard: FC<Props> = ({ project, isPc, onAction }) => {
  const { totalPrizePool, icon, participants, averageBenefits, prizeToken } =
    project || {};
  const { t } = useI18n();
  const textState = useText(project);

  const nameDisplay = textState.getNameText();

  const handleAction = () => {
    completedProjectClick();

    onAction?.(project);
  };

  if (!isPc) {
    return (
      <div className={cls(styles.projectCardH5)}>
        <div className={styles.status}>{t('ended')}</div>
        <div className={styles.headerArea}>
          <img className={styles.icon} src={getProcessedUrl(icon, {})} alt="" />
          <div className={styles.tokenName}>{nameDisplay}</div>
          <div className={styles.action}>
            <Button
              variant="secondaryBrand"
              size="small"
              onClick={handleAction}
            >
              {t('moreDetails')}
            </Button>
          </div>
        </div>
        <div className={styles.hr}></div>
        <div className={styles.contentArea}>
          <div className={styles.infoRow}>
            <div className={styles.infoItem}>
              <div className={styles.infoItemLabel}>
                {t('totalPrizePool')} ({prizeToken})
              </div>
              <div className={styles.infoItemText}>
                {safeToFormat(totalPrizePool)}
              </div>
            </div>
            <div className={styles.infoItem}>
              <div className={styles.infoItemLabel}>{t('participants')}</div>
              <div className={styles.infoItemText}>
                {safeToFormat(participants)}
              </div>
            </div>
          </div>
          <div className={styles.infoRow}>
            <div className={styles.infoItem}>
              <div className={styles.infoItemLabel}>{t('avgBenefits')}</div>
              <div className={styles.infoItemText}>
                {safeToFormat(averageBenefits, 4)} {prizeToken}
              </div>
            </div>
            <div className={styles.infoItem}>
              <div className={styles.infoItemLabel}>{t('yourRewards')}</div>
              <div className={styles.infoItemText}>
                {textState.getYourPrizeText()}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cls(styles.projectCard)}>
      <span className={styles.status}>{t('ended')}</span>
      <div className={styles.contentArea}>
        <div className={styles.brand}>
          <img className={styles.icon} src={getProcessedUrl(icon, {})} alt="" />
        </div>
        <div className={styles.body}>
          <div className={styles.title}>
            <AutoEllipsisText
              text={nameDisplay}
              textClassName={styles.text}
              maxLine={1}
            />
          </div>
          <div className={styles.info}>
            <div className={styles.infoItem}>
              <div className={styles.infoItemLabel}>
                {t('totalPrizePool')} ({prizeToken})
              </div>
              <div className={styles.infoItemText}>
                {safeToFormat(totalPrizePool)}
              </div>
            </div>
            <i className={styles.div} />
            <div className={styles.infoItem}>
              <div className={styles.infoItemLabel}>{t('participants')}</div>
              <div className={styles.infoItemSubText}>
                {safeToFormat(participants)}
              </div>
            </div>
            <div className={styles.infoItem}>
              <div className={styles.infoItemLabel}>{t('avgBenefits')}</div>
              <div className={styles.infoItemSubText}>
                {safeToFormat(averageBenefits)} {prizeToken}
              </div>
            </div>
            <div className={styles.infoItem}>
              <div className={styles.infoItemLabel}>{t('yourRewards')}</div>
              <div className={styles.infoItemSubText}>
                {textState.getYourPrizeText()}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.action}>
          <div className={styles.actionBtn}>
            <Button
              variant="secondaryBrand"
              size="x-small"
              onClick={handleAction}
            >
              {t('moreDetails')}
              <i
                className="moly-iconfont bit-a-icon_directionright"
                style={{ paddingLeft: 4 }}
              />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(EndProjectCard);
